import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, Database, Activity, Users, Globe } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface HealthMetrics {
  databaseConnection: 'healthy' | 'warning' | 'error';
  sessionTracking: 'healthy' | 'warning' | 'error';
  activeUsers: number;
  totalSessions: number;
  averageSessionTime: number;
  lastUpdate: string;
}

interface SessionHealthCheckProps {
  onBack: () => void;
}

export const SessionHealthCheck = ({ onBack }: SessionHealthCheckProps) => {
  const [metrics, setMetrics] = useState<HealthMetrics>({
    databaseConnection: 'healthy',
    sessionTracking: 'healthy',
    activeUsers: 0,
    totalSessions: 0,
    averageSessionTime: 0,
    lastUpdate: new Date().toISOString()
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const runHealthChecks = async () => {
    setLoading(true);
    const newMetrics: HealthMetrics = {
      databaseConnection: 'healthy',
      sessionTracking: 'healthy',
      activeUsers: 0,
      totalSessions: 0,
      averageSessionTime: 0,
      lastUpdate: new Date().toISOString()
    };

    try {
      // Test database connection
      const { data: connectionTest, error: connectionError } = await supabase
        .from('website_sessions')
        .select('count', { count: 'exact', head: true });

      if (connectionError) {
        newMetrics.databaseConnection = 'error';
        console.error('Database connection test failed:', connectionError);
      }

      // Check active sessions
      const { data: activeSessions, error: activeError } = await supabase
        .from('website_sessions')
        .select('*')
        .eq('is_active', true);

      if (activeError) {
        newMetrics.sessionTracking = 'warning';
        console.error('Active sessions check failed:', activeError);
      } else {
        newMetrics.activeUsers = activeSessions?.length || 0;
      }

      // Get total sessions count
      const { count: totalCount, error: countError } = await supabase
        .from('website_sessions')
        .select('*', { count: 'exact', head: true });

      if (!countError) {
        newMetrics.totalSessions = totalCount || 0;
      }

      // Calculate average session time from sessions with duration
      const { data: completedSessions, error: durationError } = await supabase
        .from('website_sessions')
        .select('duration_minutes')
        .not('duration_minutes', 'is', null);

      if (!durationError && completedSessions?.length) {
        const totalDuration = completedSessions.reduce((acc, session) => 
          acc + (session.duration_minutes || 0), 0
        );
        newMetrics.averageSessionTime = Math.round(totalDuration / completedSessions.length);
      }

      // Test session event logging with proper session
      const testSessionToken = 'health_check_' + Date.now();
      const testVisitorId = 'health_check_visitor_' + Date.now();
      
      // First create a test session
      const testSession = {
        session_token: testSessionToken,
        visitor_id: testVisitorId,
        user_agent: 'Health Check Test',
        referrer: null,
        is_active: true
      };

      const { error: sessionError } = await supabase
        .from('website_sessions')
        .insert(testSession);

      if (sessionError) {
        newMetrics.sessionTracking = 'warning';
        console.error('Session creation test failed:', sessionError);
      } else {
        // Now test session event logging
        const testEvent = {
          session_token: testSessionToken,
          visitor_id: testVisitorId,
          event_name: 'health_check',
          event_data: { test: true },
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          page_title: 'Health Check'
        };

        const { error: eventError } = await supabase
          .from('session_events')
          .insert(testEvent);

        if (eventError) {
          newMetrics.sessionTracking = 'warning';
          console.error('Session event test failed:', eventError);
        }

        // Clean up test event and session
        await supabase
          .from('session_events')
          .delete()
          .eq('session_token', testSessionToken);
          
        await supabase
          .from('website_sessions')
          .delete()
          .eq('session_token', testSessionToken);
      }

    } catch (error) {
      console.error('Health check failed:', error);
      newMetrics.databaseConnection = 'error';
      newMetrics.sessionTracking = 'error';
    }

    setMetrics(newMetrics);
    setLoading(false);
  };

  useEffect(() => {
    runHealthChecks();
  }, []);

  const getStatusIcon = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };

  const getStatusBadge = (status: 'healthy' | 'warning' | 'error') => {
    const variants = {
      healthy: 'default',
      warning: 'secondary',
      error: 'destructive'
    } as const;
    
    return (
      <Badge variant={variants[status]}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">System Health Check</h2>
          <p className="text-muted-foreground">Monitor session tracking and database connectivity</p>
        </div>
        <div className="flex space-x-2">
          <Button onClick={runHealthChecks} disabled={loading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>

      {/* Health Status Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Database</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              {getStatusIcon(metrics.databaseConnection)}
              {getStatusBadge(metrics.databaseConnection)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Session Tracking</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              {getStatusIcon(metrics.sessionTracking)}
              {getStatusBadge(metrics.sessionTracking)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.activeUsers}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalSessions}</div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>System Metrics</CardTitle>
          <CardDescription>
            Detailed performance and health information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Average Session Duration
              </label>
              <div className="text-2xl font-bold">
                {metrics.averageSessionTime} minutes
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Last Health Check
              </label>
              <div className="text-sm">
                {new Date(metrics.lastUpdate).toLocaleString()}
              </div>
            </div>
          </div>

          {/* System Status Summary */}
          <div className="pt-4 border-t">
            <h4 className="font-medium mb-2">System Status</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span>Database Connection</span>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(metrics.databaseConnection)}
                  <span className="capitalize">{metrics.databaseConnection}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span>Session Tracking</span>
                <div className="flex items-center space-x-2">
                  {getStatusIcon(metrics.sessionTracking)}
                  <span className="capitalize">{metrics.sessionTracking}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};